/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect } from 'react';
import type { LoginResponse, User } from '@gokrew/models/auth';
import type { EmployeeProfile } from '@gokrew/models/employee-profile';
import {
  getMe,
  getProfile,
  logoutUser,
  updateEmployeeLanguage,
} from '@/api/auth';
import {
  consumeIntentionalLanguage,
  clearIntentionalLanguage,
} from '@gokrew/ui/utility/intentional-language';
import { stopMockLogin } from '@/api/mock-login';
import { getTimesheetStatus } from '@/api/timesheet';
import { tokenManager } from '@/auth';
import { sessionPrewarm } from '@/auth/sessionPrewarm';
import { decodeAccessTokenClaims } from '@gokrew/ui/auth';
import { clearSurveyDrafts } from '@/utils/survey-draft-storage';
import toast from 'react-hot-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';

export interface EmployeePortalAuthContextType {
  token: string | null;
  sessionId: string | null;
  user: EmployeeProfile | null;
  isInitializing: boolean;
  isLoggingOut: boolean;
  isMockLogin: boolean;
  mockLoginSessionId: string | null;
  mockLoginExpiresAt: number | null;
  setAuth: (data: LoginResponse) => void;
  setMockLoginToken: (accessToken: string) => void;
  endMockSession: () => Promise<void>;
  logout: () => Promise<void>;
  fetchUser: () => Promise<void>;
  currentUser: User | null;
  fetchCurrentUser: () => Promise<void>;
  timesheetEnabled: boolean;
}

const EmployeePortalAuthContext = createContext<
  EmployeePortalAuthContextType | undefined
>(undefined);

export function EmployeePortalAuthProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const queryClient = useQueryClient();
  const [token, setToken] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(
    localStorage.getItem('employee_portal_sessionId')
  );
  const [user, setUser] = useState<EmployeeProfile | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [timesheetEnabled, setTimesheetEnabled] = useState<boolean>(false);
  const [isMockLogin, setIsMockLogin] = useState(false);
  const [mockLoginExpiresAt, setMockLoginExpiresAt] = useState<number | null>(
    null
  );
  const [mockLoginSessionId, setMockLoginSessionId] = useState<string | null>(
    sessionStorage.getItem('employee_portal_mock_session_id')
  );
  const { t } = useTranslation('common');
  const fetchUser = async () => {
    if (!tokenManager.getToken()) {
      setUser(null);
      return;
    }
    try {
      const profileResponse = await getProfile();
      setUser(profileResponse.data);
      const savedLanguage = profileResponse.data.user?.language || 'en';
      const intentionalLanguage = consumeIntentionalLanguage();
      if (intentionalLanguage && intentionalLanguage !== savedLanguage) {
        i18n.changeLanguage(intentionalLanguage);
        updateEmployeeLanguage(intentionalLanguage).catch(() => {});
      } else if (savedLanguage !== i18n.language) {
        i18n.changeLanguage(savedLanguage);
      }
    } catch (err) {
      console.error('Failed to fetch user profile', err);
      setUser(null);
    }
  };

  async function fetchCurrentUser() {
    try {
      const response = await getMe();
      setCurrentUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
    }
  }

  const fetchTimesheetStatus = async () => {
    try {
      const response = await getTimesheetStatus();
      setTimesheetEnabled(response?.data?.enabled || false);
    } catch (err) {
      console.error('Failed to fetch timesheet status', err);
    }
  };

  useEffect(() => {
    if (token) {
      fetchUser();
      fetchCurrentUser();
      fetchTimesheetStatus();
    }
  }, [token]);

  // Register session-expired callback
  useEffect(() => {
    tokenManager.onSessionExpired(() => {
      const wasMockLogin = !!sessionStorage.getItem(
        'employee_portal_mock_token'
      );
      tokenManager.clearToken();
      setToken(null);
      setSessionId(null);
      setUser(null);
      setCurrentUser(null);
      setIsMockLogin(false);
      setMockLoginSessionId(null);
      setMockLoginExpiresAt(null);
      localStorage.removeItem('employee_portal_sessionId');
      sessionStorage.removeItem('employee_portal_mock_token');
      sessionStorage.removeItem('employee_portal_mock_session_id');
      clearSurveyDrafts();
      clearIntentionalLanguage();
      queryClient.clear();

      if (wasMockLogin) {
        window.location.href = '/mock-session-expired';
      } else {
        toast.error('Session expired. Please login again.');
        setTimeout(() => {
          window.location.href = '/login';
        }, 1200);
      }
    });
  }, [queryClient]);

  // Silently restore session on mount (page refresh / new tab)
  useEffect(() => {
    async function restoreSession() {
      try {
        const mockToken = sessionStorage.getItem('employee_portal_mock_token');
        if (mockToken) {
          tokenManager.setToken(mockToken);
          if (tokenManager.isTokenValid()) {
            const { sessionId: sid, expiresAt } =
              decodeAccessTokenClaims(mockToken);
            setToken(mockToken);
            setIsMockLogin(true);
            setMockLoginSessionId(sid);
            setMockLoginExpiresAt(expiresAt);
          } else {
            sessionStorage.removeItem('employee_portal_mock_token');
            sessionStorage.removeItem('employee_portal_mock_session_id');
            clearSurveyDrafts();
            clearIntentionalLanguage();
            setToken(null);
            window.location.href = '/mock-session-expired';
          }
          return;
        }

        if (!localStorage.getItem('employee_portal_sessionId')) {
          setToken(null);
          return;
        }

        // Collects the refresh main.tsx already started, so it is not repeated.
        const restored = await (sessionPrewarm.take() ??
          tokenManager.getValidToken());
        setToken(restored);
      } finally {
        setIsInitializing(false);
      }
    }

    restoreSession();
  }, []);

  const setAuth = (data: LoginResponse) => {
    const { accessToken } = data.data;

    if (!accessToken) {
      console.error('Invalid auth response data:', data);
      return;
    }

    // Clear any cached data from a previous session before adopting the new
    queryClient.clear();
    clearSurveyDrafts();

    const { sessionId } = decodeAccessTokenClaims(accessToken);

    tokenManager.setToken(accessToken);
    setToken(accessToken);
    setSessionId(sessionId);
    if (sessionId) localStorage.setItem('employee_portal_sessionId', sessionId);
  };

  const setMockLoginToken = (accessToken: string) => {
    // Impersonation start: the tab now acts as someone else.
    clearSurveyDrafts();
    const { sessionId, expiresAt } = decodeAccessTokenClaims(accessToken);
    tokenManager.setToken(accessToken);
    setToken(accessToken);
    setIsMockLogin(true);
    setMockLoginSessionId(sessionId);
    setMockLoginExpiresAt(expiresAt);
    sessionStorage.setItem('employee_portal_mock_token', accessToken);
    if (sessionId) {
      sessionStorage.setItem('employee_portal_mock_session_id', sessionId);
    }
  };

  const endMockSession = async () => {
    if (mockLoginSessionId) {
      try {
        await stopMockLogin(mockLoginSessionId);
      } catch (err) {
        console.error('Failed to stop mock login session', err);
      }
    }
    // Soft clear only — the caller navigates client-side to the expired page, so
    // we avoid a hard reload (which flashes the bootstrap AppLoaderPage). The
    // expired route is public, so tearing down auth state here is safe.
    tokenManager.clearToken();
    setToken(null);
    setSessionId(null);
    setUser(null);
    setCurrentUser(null);
    setIsMockLogin(false);
    setMockLoginSessionId(null);
    setMockLoginExpiresAt(null);
    sessionStorage.removeItem('employee_portal_mock_token');
    sessionStorage.removeItem('employee_portal_mock_session_id');
    clearSurveyDrafts();
    clearIntentionalLanguage();
    queryClient.clear();
  };

  const logoutMutation = useMutation({
    mutationFn: logoutUser,
  });

  const softLogout = () => {
    tokenManager.clearToken();
    setToken(null);
    setSessionId(null);
    setUser(null);
    setCurrentUser(null);
    setIsMockLogin(false);
    setMockLoginSessionId(null);
    setMockLoginExpiresAt(null);
    localStorage.removeItem('employee_portal_sessionId');
    sessionStorage.removeItem('employee_portal_mock_token');
    sessionStorage.removeItem('employee_portal_mock_session_id');
    clearSurveyDrafts();
    clearIntentionalLanguage();
    queryClient.clear();
  };

  const logout = async () => {
    try {
      await logoutMutation.mutateAsync();
    } catch (err) {
      console.error('Logout API failed', err);
    } finally {
      softLogout();
      toast.success(t('top_nav.logout_success'));
    }
  };

  return (
    <EmployeePortalAuthContext.Provider
      value={{
        token,
        sessionId,
        user,
        isInitializing,
        isLoggingOut: logoutMutation.isPending,
        isMockLogin,
        mockLoginSessionId,
        mockLoginExpiresAt,
        setAuth,
        setMockLoginToken,
        endMockSession,
        logout,
        fetchUser,
        currentUser,
        fetchCurrentUser,
        timesheetEnabled,
      }}
    >
      {children}
    </EmployeePortalAuthContext.Provider>
  );
}

export function useEmployeePortalAuth() {
  const ctx = useContext(EmployeePortalAuthContext);
  if (!ctx) {
    throw new Error(
      'useEmployeePortalAuth must be used inside EmployeePortalAuthProvider'
    );
  }
  return ctx;
}
